<script lang="ts">
	import type { TimelineEvent } from '$lib/features/timelineEvent/timelineEvent.type';
	import IconCalendarMonth from '~icons/mdi/calendar-month';

	const { timelineEvents }: { timelineEvents: TimelineEvent[] } = $props();

	const counter = $derived<number>(timelineEvents.length);
	const yearSpan = $derived.by(() => {
		if (timelineEvents.length === 0) return null;
		const years = timelineEvents.map((e) => e.year);
		const min = Math.min(...years);
		const max = Math.max(...years);
		return { min, max, span: max - min };
	});

	function draggableScroll(node: HTMLElement) {
		let startX: number;
		let scrollLeft: number;
		let isDown = false;

		const onMouseDown = (e: MouseEvent) => {
			isDown = true;
			startX = e.pageX - node.offsetLeft;
			scrollLeft = node.scrollLeft;
		};

		const onMouseLeave = () => {
			isDown = false;
		};

		const onMouseUp = () => {
			isDown = false;
		};

		const onMouseMove = (e: MouseEvent) => {
			if (!isDown) return;
			e.preventDefault();
			const x = e.pageX - node.offsetLeft;
			const walk = (x - startX) * 1.5;
			node.scrollLeft = scrollLeft - walk;
		};

		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'ArrowRight') node.scrollBy({ left: 300, behavior: 'smooth' });
			if (e.key === 'ArrowLeft') node.scrollBy({ left: -300, behavior: 'smooth' });
		};

		node.addEventListener('mousedown', onMouseDown);
		node.addEventListener('mouseleave', onMouseLeave);
		node.addEventListener('mouseup', onMouseUp);
		node.addEventListener('mousemove', onMouseMove);
		node.addEventListener('keydown', onKeyDown);

		return () => {
			node.removeEventListener('mousedown', onMouseDown);
			node.removeEventListener('mouseleave', onMouseLeave);
			node.removeEventListener('mouseup', onMouseUp);
			node.removeEventListener('mousemove', onMouseMove);
			node.removeEventListener('keydown', onKeyDown);
		};
	}
</script>

<div class="flex flex-col gap-2">
	{#if yearSpan}
		<div class="flex flex-row items-center justify-center gap-2 px-1">
			<h3 class="badge gap-1.5 badge-soft py-3 text-xs font-medium tracking-wide badge-secondary">
				<IconCalendarMonth class="text-base-content/60" />
				{yearSpan.span > 0
					? `${counter > 7 ? `${counter} ` : ''}events spanning ${yearSpan.span} year${yearSpan.span === 1 ? '' : 's'}`
					: `${counter > 7 ? `${counter} ` : ''}events from ${yearSpan.min}`}
			</h3>
		</div>
	{/if}

	<div
		{@attach draggableScroll}
		class="flex w-full cursor-grab snap-x snap-mandatory scrollbar-thin scrollbar-thumb-base-300/60 overflow-x-auto pt-2 pb-6 outline-none select-none after:block after:w-4 after:shrink-0 active:cursor-grabbing md:after:w-8"
		role="region"
		aria-label="Interactive History Timeline. Drag or use arrow keys to scroll."
		tabindex="-1"
	>
		{#each timelineEvents as event, i (event.title)}
			<div
				class="child-card pointer-events-none flex w-4/5 max-w-sm min-w-64 shrink-0 snap-start snap-always flex-col sm:w-1/2 lg:w-1/3"
			>
				{@render Event(event, { isFirst: i === 0, isLast: i === timelineEvents.length - 1 })}
			</div>
		{/each}
	</div>
</div>

{#snippet Event(event: TimelineEvent, { isFirst, isLast }: { isFirst: boolean; isLast: boolean })}
	<div class="relative flex flex-col items-center justify-end pb-2 text-center">
		<span class="text-xs leading-none font-semibold tracking-widest text-base-content/70">YEAR</span>
		<span class="text-xl leading-tight font-bold tracking-wider text-base-content md:text-2xl">
			{event.year}
		</span>
	</div>

	<div class="my-1 flex h-5 w-full items-center">
		<div class={{ 'h-0.5 flex-1 bg-base-content/15': true, invisible: isFirst }}></div>
		<div class="z-10 size-3 shrink-0 rounded-full border-2 border-secondary bg-base-100 shadow-sm"></div>
		<div class={{ 'h-0.5 flex-1 bg-base-content/15': true, invisible: isLast }}></div>
	</div>

	<div class="h-full flex-1 px-2 pt-2 md:px-3">
		<div
			class="h-full rounded-2xl border border-base-content/10 bg-base-200/50 p-5 shadow-xs transition-all hover:border-base-content/20 hover:bg-base-200/80 md:p-6"
		>
			<h3 class="mb-2 text-base leading-snug text-base-content md:text-lg">{event.title}</h3>
			<p class="text-sm leading-relaxed text-base-content/75">{event.description}</p>
		</div>
	</div>
{/snippet}
